import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { type Closing, Return, Stewardship } from '@accounts/domain';
import { Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Badge, Button, Callout, Dialog, EmptyState, EndLine, Field, Form, ListRow, OrganizationMark, useRowKeys } from '@beyond-js/ui/react';
import { Outage, Problem } from '@accounts/ui';

/**
 * The organizations a person belongs to, and creating one.
 *
 * Creation is an explicit action that opens a focused form, not a field pinned beside the list: the
 * shared pattern of the family is list, detail, and an explicit create, and a permanent form next to
 * a list makes the list harder to scan for the far more common case of just looking.
 *
 * `?view=create` opens that form directly, which is how a product that has no organization to show
 * brings someone straight to making one. Creating it is then the task that product sent the person
 * for, so once it is created they go back to it by themselves (D54) when the service validated the
 * way back, carrying `done=organization&organization=<id>` so the product opens the new one; otherwise,
 * and when the form was opened here, they go to the new organization. While the name is empty Create waits
 * quietly and a press puts the caret in it, saying nothing (D183).
 *
 * The organizations come with the person's identity; the ones scheduled for deletion are the page's one
 * read, and Accounts not answering it is said once (`Outage`, D163) rather than read as none. Known only
 * once that read answers, they come after the list already drawn, so their arrival moves nothing (D185).
 *
 * Two organizations with one name are told apart as the family bar tells them apart (D195): each row of a
 * shared name says its short name. Each row starts with its organization mark, using its initial without a logo.
 */
export function OrganizationList() {
	const { text, date, identity, client, organizations, refresh, sender, language } = usePresence();
	const navigate = useNavigate();
	const stewardship = useMemo(() => new Stewardship(client), [client]);
	const arrived = useLocation().state as { notice?: string; left?: string } | null;
	const [closing, setClosing] = useState<Closing[]>([]);
	const [unread, setUnread] = useState<Refusal>();
	const [duplicate, setDuplicate] = useState(false);
	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(() => Refusal.answered(async () => setClosing(await stewardship.closing()), setUnread), [stewardship]);
	useEffect(() => {
		void read();
	}, [read, identity]);

	const [creating, setCreating] = useState(false);
	const [name, setName] = useState('');
	const [busy, setBusy] = useState(false);
	// Said with `Problem`: an outage in the warning tone, never as a failure (D40, D132, D133, D186)
	const [failure, setFailure] = useState<unknown>();

	const memberships = identity?.organizations ?? [];
	// A role is not repeated on rows that all share it (L3)
	const differ = memberships.length < 2 || new Set(memberships.map(membership => membership.role)).size > 1;
	// A name two organizations share, ignoring case and outer spaces, says each one's short name (D195)
	const named = (membership: { name?: string; organization: string }) => (membership.name ?? membership.organization).trim().toLocaleLowerCase();
	const shared = new Set(memberships.map(named).filter((key, index, keys) => keys.indexOf(key) !== index));
	// Up and Down move between the rows (L7)
	const keys = useRowKeys();

	const [parameters, setParameters] = useSearchParams();
	const view = parameters.get('view');
	// Read as the address arrived, before the request to be returned is taken out of it: a create form a
	// product asked for, not one Accounts' own bar opened (`product=accounts`)
	const [task] = useState(() => view === 'create' && Return.requested(parameters) && parameters.get(Return.PRODUCT) !== Return.OWN);
	useEffect(() => {
		if (view !== 'create') return;
		setCreating(true);
		// Consumed once: a reload or Back should show the list, not reopen the form
		const rest = new URLSearchParams(parameters);
		rest.delete('view');
		setParameters(rest, { replace: true });
	}, [parameters, setParameters, view]);

	/**
	 * @param again Creates it even though the person already has an organization of that name, after the
	 * warning (ACC-20)
	 */
	async function create(again = false) {
		// An empty name never leaves: `Form` keeps the caret in it, and nothing else creates without one
		if (!name.trim()) return;
		setBusy(true);
		setFailure(undefined);
		try {
			const created = await organizations.create(name.trim(), again);
			setDuplicate(false);
			setCreating(false);
			setName('');
			await refresh();
			// The task a product sent the person for is done: back to it with the new organization, which it
			// reads and switches to (CNT-03), and where the bar reads the standing again
			if (task && sender) {
				globalThis.location.assign(Return.done(sender.destination, 'organization', { organization: created.id }));
				return;
			}
			// The detail says it was created, under the arrival line back to a product that sent the person
			navigate(`/organizations/${created.id}`, { state: { created: true } });
		} catch (error) {
			// Withheld by the page's session, which answers the person: the form stays as it was
			if (Refusal.is(error) && error.quiet) return;
			if (Refusal.is(error) && error.code === 'NAME_DUPLICATE') {
				setDuplicate(true);
				return;
			}
			setFailure(error);
			// Nothing came back, so the organization may exist: the list behind the dialog is read again
			if (Refusal.is(error) && error.unknown) await refresh();
		} finally {
			setBusy(false);
		}
	}

	/** Plain clicks stay in Accounts; modified clicks retain native link behavior. */
	function open(organization: string, event: Event) {
		const click = event as MouseEvent;
		if (click.defaultPrevented || click.button !== 0 || click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return;
		click.preventDefault();
		navigate(`/organizations/${organization}`);
	}

	return (
		<Screen
			template="list"
			width="fluid"
			title={text('organizations.title')}
			lead={text('organizations.lead')}
			actions={
				<Button
					variant="primary"
					onClick={() => {
						setFailure(undefined);
						setCreating(true);
					}}
				>
					{text('organizations.create')}
				</Button>
			}
		>
			<Outage reads={[{ key: 'closing', title: text('outage.closing'), unread, run: read }]} />
			{failure && !creating ? <Problem error={failure} /> : null}
			{arrived?.notice ? <Callout tone="success" live title={arrived.notice} /> : null}
			{arrived?.left ? <Callout tone="success" live title={text('organization.left', { organization: arrived.left })} /> : null}

			{memberships.length === 0 ? (
				<EmptyState title={text('organizations.none.title')} text={text('organizations.none.body')} labels={EmptyState.labels[language]} />
			) : (
				<>
					<ul ref={keys} className="bui-rows" data-framed aria-label={text('organizations.title')}>
						{memberships.map(membership => (
							<ListRow key={membership.organization} title={membership.name ?? membership.organization}
								href={`/organizations/${membership.organization}`} run={event => open(membership.organization, event)}
								lead={<OrganizationMark name={membership.name ?? ''} logo={membership.logo ?? null} />}
								inline
								meta={differ || shared.has(named(membership)) ? <>{differ ? <Badge label={text(`role.${membership.role}`)} /> : null}{shared.has(named(membership)) ? <span>{membership.slug}</span> : null}</> : null}
								labels={ListRow.labels[language]} />
						))}
					</ul>
					<EndLine labels={EndLine.labels[language]} />
				</>
			)}

			{/* Organizations scheduled for deletion are their own group, named once by its label (L4) */}
			{closing.length ? (
				<div className="mt-6">
					<p data-bui-label className="text-small text-ink-muted">
						{text('closing.title')}
					</p>
					<ul className="bui-rows" data-framed aria-label={text('closing.title')}>
						{closing.map(entry => (
							<ListRow key={entry.organization} title={entry.name} href={`/organizations/${entry.organization}`}
								run={event => open(entry.organization, event)} labels={ListRow.labels[language]}
								lead={{ state: 'warning' }} meta={text('closing.lead', { date: date(entry.purge) })} />
						))}
					</ul>
				</div>
			) : null}

			{/* Cancelling keeps nothing: an organization name is not work worth preserving, and a stale
			    draft reappearing later is more confusing than an empty field */}
			<Dialog
				open={creating}
				title={text('organizations.create.title')}
				busy={busy}
				labels={Dialog.labels[language]}
				onClose={() => {
					setCreating(false);
					setName('');
					setDuplicate(false);
				}}
			>
				<Form
					className="flex flex-col gap-4"
					onSubmit={event => {
						event.preventDefault();
						void create();
					}}
				>
					<Field label={text('organizations.name')} hint={text('organizations.name.help')}>
						<input
							value={name}
							maxLength={120}
							required
							onChange={event => {
								setName(event.target.value);
								setDuplicate(false);
							}}
						/>
					</Field>
					{/* Explained inside the dialog, where the person is looking, not behind it */}
					{failure ? <Problem error={failure} /> : null}
					{duplicate ? (
						<Callout
							tone="warning"
							live
							title={text('organizations.duplicate', { name: name.trim() })}
							actions={
								<Button type="button" variant="secondary" busy={busy} onClick={() => void create(true)}>
									{text('organizations.duplicate.continue')}
								</Button>
							}
						/>
					) : null}
					<div className="flex justify-end gap-2">
						<Button type="button" variant="quiet" onClick={() => setCreating(false)}>
							{text('action.cancel')}
						</Button>
						<Button type="submit" variant="primary" busy={busy} labels={{ busy: text('action.saving') }}>
							{text('action.create')}
						</Button>
					</div>
				</Form>
			</Dialog>
		</Screen>
	);
}
